import { useCallback, useEffect, useRef } from 'react';

export function useRafBatcher() {
  const rafIdRef = useRef(0);
  const pendingRef = useRef(false);
  const batchDepthRef = useRef(0);
  const queuedFnRef = useRef(null);

  const flush = useCallback(() => {
    pendingRef.current = false;
    rafIdRef.current = 0;

    const fn = queuedFnRef.current;
    queuedFnRef.current = null;

    if (fn) fn();
  }, []);

  const schedule = useCallback(
    (fn) => {
      queuedFnRef.current = fn;

      if (batchDepthRef.current > 0) return;

      if (pendingRef.current) return;
      pendingRef.current = true;

      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(flush);
    },
    [flush],
  );

  const beginBatch = useCallback(() => {
    batchDepthRef.current += 1;
  }, []);

  const endBatch = useCallback(() => {
    batchDepthRef.current = Math.max(0, batchDepthRef.current - 1);
    if (batchDepthRef.current === 0 && queuedFnRef.current) {
      if (!pendingRef.current) {
        pendingRef.current = true;
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = requestAnimationFrame(flush);
      }
    }
  }, [flush]);

  useEffect(() => {
    return () => cancelAnimationFrame(rafIdRef.current);
  }, []);

  return { schedule, beginBatch, endBatch };
}
